import { useMemo, useState } from "react";
import { Box, Chip, Stack, Typography } from "@mui/material";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import { useRegistry } from "../../context/RegistryContext";
import { EntityAvatar } from "./EntityAvatar";
import { EntityPickerModal } from "./EntityPickerModal";
import { ENTITY_LABELS, hasAvatar, type PickerKind } from "./config";
import type { PickerEntity } from "./entity";

type CommonOptions = {
  label?: string;
  required?: boolean;
  size?: "small" | "medium";
  fullWidth?: boolean;
  includeHidden?: boolean;
  allowCreate?: boolean;
  items?: PickerEntity[];
  groupBy?: (item: PickerEntity) => string;
};

type SingleProps<TId extends string> = CommonOptions & {
  multiple?: false;
  value: TId | null;
  /**
   * The picked id, and the entity it names — use the entity, not a registry
   * lookup of the id, to read anything else about it: an entity created
   * inline isn't in the caller's registry snapshot yet.
   */
  onChange: (next: TId | null, entity: PickerEntity | null) => void;
  allowEmpty?: boolean;
  emptyLabel?: string;
};

type MultiProps<TId extends string> = CommonOptions & {
  multiple: true;
  value: TId[];
  onChange: (next: TId[]) => void;
};

export type TypedEntityPickerProps<TId extends string> =
  | SingleProps<TId>
  | MultiProps<TId>;

export type EntityPickerFieldProps<TId extends string> =
  TypedEntityPickerProps<TId> & { kind: PickerKind };

export const EntityPickerField = <TId extends string>(
  props: EntityPickerFieldProps<TId>,
) => {
  const {
    kind,
    label,
    required = false,
    size = "medium",
    fullWidth = true,
    includeHidden = false,
    allowCreate,
    items,
    groupBy,
    multiple = false,
  } = props;

  const reg = useRegistry();
  // The current value is named from the full registry, hidden entries
  // included. The modal still offers only visible ones (unless
  // `includeHidden`), but a value the user can no longer pick — an invoice's
  // hidden payee, a hidden default group — is still the value, so it shows by
  // name rather than as the placeholder.
  const registryEntities = useMemo<PickerEntity[]>(() => {
    switch (kind) {
      case "source":
        return reg.withHidden.sources as unknown as PickerEntity[];
      case "group":
        return reg.withHidden.groups as unknown as PickerEntity[];
      case "category":
        return reg.withHidden.categories as unknown as PickerEntity[];
      case "expense-type":
        return reg.withHidden.expenseTypes as unknown as PickerEntity[];
      case "payee":
        return reg.withHidden.payees as unknown as PickerEntity[];
      case "invoice":
        return reg.invoices as unknown as PickerEntity[];
      case "auto-transaction":
        // Rules don't live in the registry; the wrapper always supplies items.
        return [];
    }
  }, [kind, reg]);

  const known = items ?? registryEntities;

  const effectiveLabel = label ?? ENTITY_LABELS[kind];
  const inlineCreate = allowCreate ?? !multiple;

  const [open, setOpen] = useState(false);

  const selectedSingle: PickerEntity | null = !multiple
    ? (known.find((e) => e.id === (props as SingleProps<TId>).value) ?? null)
    : null;

  const selectedMulti: PickerEntity[] = multiple
    ? (props as MultiProps<TId>).value
        .map((id) => known.find((e) => e.id === id))
        .filter((x): x is PickerEntity => x !== undefined)
    : [];

  // Says why the current value is missing from the list the modal opens.
  const hiddenSuffix = (entity: PickerEntity) =>
    entity.hidden && !includeHidden ? " (hidden)" : "";

  const allowEmpty =
    !multiple && (props as SingleProps<TId>).allowEmpty === true;
  const emptyLabel = !multiple
    ? (props as SingleProps<TId>).emptyLabel
    : undefined;

  const placeholder = multiple
    ? `Select ${effectiveLabel.toLowerCase()}`
    : allowEmpty
      ? (emptyLabel ?? "All")
      : `Select ${effectiveLabel.toLowerCase()}`;

  const minHeight = size === "small" ? 40 : 56;

  const handleOpen = () => setOpen(true);
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      setOpen(true);
    }
  };

  const showAvatar = hasAvatar(kind);

  const renderContent = () => {
    if (!multiple) {
      return selectedSingle ? (
        <Stack
          direction="row"
          spacing={1}
          sx={{ alignItems: "center", flex: 1, minWidth: 0 }}
        >
          {showAvatar && (
            <EntityAvatar
              kind={kind}
              entity={selectedSingle}
              size={size === "small" ? 22 : 24}
            />
          )}
          <Typography
            noWrap
            sx={{
              fontWeight: selectedSingle.style === "bold" ? "bold" : undefined,
              fontStyle:
                selectedSingle.style === "italic" ? "italic" : undefined,
            }}
          >
            {selectedSingle.name}
            {hiddenSuffix(selectedSingle) && (
              <Typography
                component="span"
                sx={{ color: "text.secondary", fontWeight: "normal" }}
              >
                {hiddenSuffix(selectedSingle)}
              </Typography>
            )}
          </Typography>
        </Stack>
      ) : (
        <Typography noWrap color="text.secondary" sx={{ flex: 1 }}>
          {placeholder}
        </Typography>
      );
    }
    if (selectedMulti.length === 0) {
      return (
        <Typography noWrap color="text.secondary" sx={{ flex: 1 }}>
          {placeholder}
        </Typography>
      );
    }
    return (
      <Stack
        direction="row"
        spacing={0.5}
        useFlexGap
        sx={{
          flexWrap: "wrap",
          alignItems: "center",
          flex: 1,
          minWidth: 0,
          py: 0.25,
        }}
      >
        {selectedMulti.map((entity) => (
          <Chip
            key={entity.id}
            avatar={
              showAvatar ? (
                <EntityAvatar kind={kind} entity={entity} size={20} />
              ) : undefined
            }
            label={`${entity.name}${hiddenSuffix(entity)}`}
            size="small"
            onClick={(e) => e.stopPropagation()}
            onDelete={(e) => {
              e.stopPropagation();
              (props as MultiProps<TId>).onChange(
                (props as MultiProps<TId>).value.filter(
                  (id) => id !== entity.id,
                ),
              );
            }}
            sx={{
              bgcolor: entity.colorBg || undefined,
              color: entity.colorFg || undefined,
              fontWeight: entity.style === "bold" ? "bold" : undefined,
              fontStyle: entity.style === "italic" ? "italic" : undefined,
            }}
          />
        ))}
      </Stack>
    );
  };

  return (
    <Box sx={{ width: fullWidth ? "100%" : undefined, position: "relative" }}>
      <Box
        component="div"
        role="combobox"
        aria-label={effectiveLabel}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-required={required || undefined}
        tabIndex={0}
        onClick={handleOpen}
        onKeyDown={handleKeyDown}
        sx={{
          position: "relative",
          display: "flex",
          alignItems: "center",
          gap: 1,
          cursor: "pointer",
          border: "1px solid",
          borderColor: "rgba(0, 0, 0, 0.23)",
          borderRadius: 1,
          minHeight,
          px: 1.5,
          py: size === "small" ? 0.75 : 1,
          bgcolor: "background.paper",
          transition: "border-color 120ms",
          "&:hover": { borderColor: "text.primary" },
          "&:focus-visible": {
            outline: "none",
            borderColor: "primary.main",
            boxShadow: (t) => `0 0 0 1px ${t.palette.primary.main}`,
          },
        }}
      >
        <Typography
          component="span"
          sx={{
            position: "absolute",
            top: 0,
            left: 10,
            transform: "translate(0, -50%) scale(0.75)",
            transformOrigin: "top left",
            bgcolor: "background.paper",
            px: 0.5,
            fontSize: "1rem",
            color: "text.secondary",
            pointerEvents: "none",
          }}
        >
          {effectiveLabel}
          {required ? " *" : ""}
        </Typography>
        <Box
          sx={{ flex: 1, minWidth: 0, display: "flex", alignItems: "center" }}
        >
          {renderContent()}
        </Box>
        <ArrowDropDownIcon
          sx={{ color: "action.active", pointerEvents: "none" }}
        />
      </Box>
      {multiple ? (
        <EntityPickerModal<TId>
          open={open}
          onClose={() => setOpen(false)}
          kind={kind}
          title={`Select ${effectiveLabel}`}
          includeHidden={includeHidden}
          allowCreate={inlineCreate}
          items={items}
          groupBy={groupBy}
          multiple
          value={(props as MultiProps<TId>).value}
          onChange={(props as MultiProps<TId>).onChange}
        />
      ) : (
        <EntityPickerModal<TId>
          open={open}
          onClose={() => setOpen(false)}
          kind={kind}
          title={`Select ${effectiveLabel}`}
          includeHidden={includeHidden}
          allowCreate={inlineCreate}
          items={items}
          groupBy={groupBy}
          value={(props as SingleProps<TId>).value}
          onChange={(props as SingleProps<TId>).onChange}
          allowEmpty={allowEmpty}
        />
      )}
    </Box>
  );
};
